import { createQueryKeyStore } from "@lukemorales/query-key-factory";
import { skipToken } from "@tanstack/react-query";

import type { Subscription } from "@/shared/domains";
import type { MakeOptional } from "@/shared/lib";
import type { Appointment, ScheduleAppointment } from "../models";
import {
  getAllUpcomingAppointments,
  getAppointment,
  getDietitianUpcomingAppointments,
  getJourneySupportPastAppointments,
  getJourneySupportUpcomingAppointments,
  getMedicationRequestPastAppointments,
  getMedicationRequestUpcomingAppointments,
} from "./appointments";
import { getNeedsSyncRefill } from "./patients";
import type { GetAvailabilityParams, GetProvidersParams } from "./providers";
import {
  getAvailableDays,
  getAvailableTimeSlots,
  getProviders,
} from "./providers";

export const consultationsQueriesStore = createQueryKeyStore({
  consultations: {
    appointments: {
      queryKey: null,
      contextQueries: {
        detail: (
          id?: Appointment["id"],
          params?: {
            subscriptionId?: Subscription["id"];
            appointmentType: ScheduleAppointment["type"];
          },
        ) => ({
          queryKey: [{ id, ...params }],
          queryFn:
            id && params
              ? () => getAppointment({ id, ...params })
              : (skipToken as never),
        }),

        list: {
          queryKey: null,
          contextQueries: {
            past: {
              queryKey: null,
              contextQueries: {
                journeySupport: {
                  queryKey: null,
                  queryFn: getJourneySupportPastAppointments,
                },
                medicationRequest: (offSet: number) => ({
                  queryKey: [{ offSet }],
                  queryFn: () => getMedicationRequestPastAppointments(offSet),
                }),
              },
            },
            upcoming: {
              queryKey: null,
              queryFn: getAllUpcomingAppointments,
              contextQueries: {
                journeySupport: {
                  queryKey: null,
                  queryFn: getJourneySupportUpcomingAppointments,
                },
                medicationRequest: {
                  queryKey: null,
                  queryFn: getMedicationRequestUpcomingAppointments,
                },
                dietitian: {
                  queryKey: null,
                  queryFn: getDietitianUpcomingAppointments,
                },
              },
            },
          },
        },
      },
    },

    providers: {
      queryKey: null,
      contextQueries: {
        list: {
          queryKey: null,
          contextQueries: {
            openLoop: ({
              state,
              ...params
            }: MakeOptional<GetProvidersParams, "state">) => ({
              queryKey: [{ state, ...params }],
              queryFn: state
                ? () => getProviders({ state, ...params })
                : (skipToken as never),
            }),
          },
        },
        availability: {
          queryKey: null,
          contextQueries: {
            days: (params: GetAvailabilityParams) => ({
              queryKey: [{ params }],
              queryFn: () => getAvailableDays(params),
            }),

            timeSlots: ({
              date,
              ...params
            }: MakeOptional<GetAvailabilityParams, "date">) => ({
              queryKey: [{ date, ...params }],
              queryFn: date
                ? () => getAvailableTimeSlots({ date, ...params })
                : (skipToken as never),
            }),
          },
        },
      },
    },

    patients: {
      queryKey: null,
      contextQueries: {
        needsSyncRefill: {
          queryKey: null,
          queryFn: getNeedsSyncRefill,
        },
      },
    },
  },
});
